import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import LiveChat from "./components/LiveChat";
import AnalyticsGate from "./components/AnalyticsGate";
import AnalyticsProvider from "./components/AnalyticsProvider";
import CookieBanner from "./components/CookieBanner";
import { initGA } from "./lib/analytics";
import { hasAnalyticsConsent } from "./lib/consent";
import { initializePerformanceOptimizations } from "./lib/performance";
import { lazy, Suspense, useEffect } from "react";

// Internal analytics dashboard pulls in the chart library; load it only on /analytics
const AnalyticsDashboard = lazy(() => import("./components/AnalyticsDashboard"));

// Route-level code splitting: each page loads only its own code (home stays in the main bundle)
const Services = lazy(() => import("./pages/Services"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => {
  // Initialize analytics and performance optimizations
  useEffect(() => {
    // Google Analytics only after the visitor has allowed statistics (cookie banner)
    if (hasAnalyticsConsent()) initGA();
    initializePerformanceOptimizations();
  }, []);

  return (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AnalyticsProvider />
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/services" element={<Services />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route
              path="/analytics"
              element={
                <AnalyticsGate>
                  <Suspense fallback={null}>
                    <AnalyticsDashboard />
                  </Suspense>
                </AnalyticsGate>
              }
            />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          {/* LiveChat is disabled for now to prevent unnecessary analytics calls */}
          {/* <div className="hidden">
            <LiveChat />
          </div> */}
          <CookieBanner />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
